'use client';

import { useEffect, useRef, useState } from 'react';
import { TipBubble, type TipAnchor } from './tip-bubble';

/**
 * Every `title` in the product, in the product's own tooltip. The attribute is
 * lifted off the hovered element — which is what stops the browser drawing its
 * own — and put back on leave, so nothing loses what a reader or a test reads.
 */
/*
  Three rules, each from watching it misbehave on the case board:

  - it waits. A bubble that appears the instant a cursor crosses something
    follows the cursor across a dense board like a fly.
  - it does not move while it is open. Re-anchoring on every element under the
    cursor is what made it jump; a new label replaces the old one only after
    the cursor has settled somewhere else for the same wait.
  - it is anchored to the cursor on anything big. Centred over a 300px card the
    bubble lands nowhere near what the reader is pointing at.
*/
/* Short of a second, which is deliberate: long enough that a cursor merely
   passing through on its way somewhere else does not trip it, short enough
   that asking "what's this column" does not feel like waiting. */
const WAIT_MS = 900;
/** Wider than this and the middle of it is not where the reader is looking. */
const BIG = 220;

interface Shown {
  text: string;
  anchor: TipAnchor;
}

export function TitleTips() {
  const [shown, setShown] = useState<Shown | null>(null);
  /* The element whose title is on loan, the text taken off it, and the timer
     that has not fired yet. Refs, because every listener has to reach the same
     one and none of them re-renders. */
  const host = useRef<Element | null>(null);
  const borrowed = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function cancel() {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    }

    function giveBack() {
      cancel();
      if (host.current && borrowed.current !== null) {
        host.current.setAttribute('title', borrowed.current);
        host.current.removeAttribute('data-tipped');
      }
      host.current = null;
      borrowed.current = null;
      setShown(null);
    }

    function place(next: Element, text: string, x: number, y: number, atCursor: boolean) {
      const box = next.getBoundingClientRect();
      /* Big things are labelled at the cursor: centred over a 300px card the
         bubble lands nowhere near what the reader is pointing at. Keyboard
         focus has no cursor, so it is always the element's own middle. A
         cursor anchor is a point, and the bubble clears it by the row it is
         in rather than by the height of the whole card. */
      const wide = atCursor && (box.width > BIG || box.height > 80);
      setShown({
        text,
        anchor: wide
          ? { top: y, bottom: y, cx: x }
          : { top: box.top, bottom: box.bottom, cx: box.left + box.width / 2 },
      });
    }

    /*
      Whatever is under the cursor and wants a label: a `title` still on the
      page, or the element whose title is currently on loan — marked, because
      the attribute is off it while it is hovered and `[title]` no longer finds
      it. Innermost wins, which is what `closest` gives.
    */
    function wants(target: EventTarget | null): Element | null {
      if (!(target instanceof Element)) return null;
      return target.closest('[title], [data-tipped]');
    }

    function take(next: Element, x: number, y: number, atCursor = true) {
      const text = next.getAttribute('title')?.trim();
      if (!text) return;
      host.current = next;
      borrowed.current = text;
      next.removeAttribute('title');
      next.setAttribute('data-tipped', '');
      timer.current = setTimeout(() => {
        timer.current = null;
        if (host.current === next) place(next, text, x, y, atCursor);
      }, WAIT_MS);
    }

    const onMove = (event: Event) => {
      const at = event as MouseEvent;
      const next = wants(at.target);
      // Already on this one: nothing moves. The label belongs to what the
      // reader stopped on, and a bubble that follows the cursor is the
      // jumping this waits out.
      if (next && next === host.current) return;
      if (host.current) giveBack();
      if (next) take(next, at.clientX, at.clientY);
    };

    const onFocus = (event: Event) => {
      const next = wants(event.target);
      if (!next || next === host.current) return;
      giveBack();
      take(next, 0, 0, false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') giveBack();
    };

    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerdown', giveBack, true);
    document.addEventListener('focusin', onFocus, true);
    document.addEventListener('focusout', giveBack, true);
    document.addEventListener('keydown', onKey, true);
    // A label anchored to a row that has scrolled away points at nothing.
    window.addEventListener('scroll', giveBack, true);
    window.addEventListener('resize', giveBack);
    return () => {
      giveBack();
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerdown', giveBack, true);
      document.removeEventListener('focusin', onFocus, true);
      document.removeEventListener('focusout', giveBack, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', giveBack, true);
      window.removeEventListener('resize', giveBack);
    };
  }, []);

  if (!shown) return null;
  return <TipBubble text={shown.text} anchor={shown.anchor} />;
}
